import { useEffect, useState } from "react";
import { getWeather } from "../lib/api";

/** Live National Weather Service forecast for one marina.
 *
 *  The banner renders ONLY what the NWS feed returned. If the feed is
 *  unavailable the whole block is removed from the page rather than shown with
 *  placeholders -- a boat renter must never read a temperature or a wind speed
 *  that we made up. No advisory or safety wording is added here; every phrase
 *  ("Patchy Fog", "Slight Chance Showers") is NWS's own.
 */
export const WeatherBanner = ({ location = "west-branch", title }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    getWeather(location)
      .then((d) => live && setData(d?.available ? d : null))
      .catch(() => live && setData(null));
    return () => {
      live = false;
    };
  }, [location]);

  if (!data) return null;

  const periods = data.periods || [];

  return (
    <section data-testid="weather-banner" className="bg-navy py-16 lg:py-20">
      <div className="mx-auto max-w-[88rem] px-5 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-lake">Weather</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold text-white lg:text-4xl">
              {title}
            </h2>
          </div>
          {data.place && (
            <p data-testid="weather-place" className="text-xs font-semibold text-white/50">
              {data.place} · {data.attribution}
            </p>
          )}
        </div>

        <ul
          data-testid="weather-forecast"
          className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        >
          {periods.map((p) => (
            <li
              key={p.name}
              data-testid={`weather-period-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-white/10 bg-white/[0.05] p-6"
            >
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-lake">{p.name}</p>
              <p className="mt-4 font-display text-4xl font-extrabold text-white">
                {p.temperature}°{p.temperatureUnit}
              </p>
              <p className="mt-3 text-sm text-white/80">{p.shortForecast}</p>
              <p className="mt-4 text-xs text-white/50">
                {p.windSpeed} {p.windDirection}
                {p.probabilityOfPrecipitation !== null &&
                  p.probabilityOfPrecipitation !== undefined &&
                  ` · ${p.probabilityOfPrecipitation}% precip`}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default WeatherBanner;
